import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import type { FilterProps, DropdownOptionProps } from './Filter.types'
import './filter.scss'
import Checkfield from '../checkfield/Checkfield'
import Icon from '../icon/Icon'

const Filter = ({
  choices,
  label,
  chips,
  searchable,
  inline,
  placeholder,
  columns,
  onChange,
}: FilterProps) => {
  const [title, setTitle] = useState(placeholder)
  const [open, setOpen] = useState(false)
  const [focus, setFocus] = useState(false)
  const [searching, setSearching] = useState(false)
  const [stateAll, setStateAll] =
    useState<'Empty' | 'Checked' | 'Indeterminate'>('Empty')
  const [selected, setSelected] = useState<DropdownOptionProps[]>([])
  const [reorder, setReorder] = useState(choices)
  const isOpen = open ? 'sb-filter--open' : ''
  const isFocused = focus ? 'sb-filter__bottom--search--focused' : ''
  const isChips = chips ? 'sb-filter__chips' : ''
  const isInline = inline ? 'sb-filter--inline' : ''
  const isSelected = selected.length ? 'sb-filter--selected' : ''
  let half = Math.floor(choices.length / 2)

  const updateAll = (e: any) => {
    const check = e.target.checked
    const updateSelected = reorder.map((op) =>
      !op.isDisabled ? { ...op, isSelected: check } : op
    )

    setReorder(updateSelected)
  }

  const filterData = (e: any) => {
    const value = e.target.value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
    const first = reorder.filter(
      (op) =>
        op.label
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .indexOf(value.toLowerCase()) > -1
    )
    const last = reorder.filter(
      (op) =>
        op.label
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .indexOf(value.toLowerCase()) === -1
    )
    setReorder([...first, ...last])
    setSearching(value.length > 0)
  }

  const getSelectedData = () => {
    const selectedItems = reorder.filter((op) => op.isSelected ?? false)
    const availableItems = reorder.filter((op) => !op.isDisabled ?? false)

    if (selectedItems.length) {
      setTitle(`${selectedItems.length} seleccionados de ${choices.length}`)
    } else {
      setTitle(placeholder)
    }

    if (selectedItems.length >= 1) {
      setStateAll('Indeterminate')
    }
    if (selectedItems.length === availableItems.length) {
      setStateAll('Checked')
    }
    if (selectedItems.length === 0) {
      setStateAll('Empty')
    }

    setSelected(selectedItems)
    onChange(reorder)
    // getSelected(selected);
  }

  const updateSelected = (event: any, value: string, check: boolean) => {
    event.preventDefault()
    event.stopPropagation()

    const selectedItems = reorder.map((op) =>
      op.value === value ? { ...op, isSelected: check } : op
    )

    setReorder(selectedItems)
  }

  useEffect(() => {
    getSelectedData()
    // eslint-disable-next-line
  }, [reorder])

  const RenderOptions = ({ choices }: FilterProps) => {
    return (
      <>
        {choices.map((choice, index) => (
          <div
            className={`sb-filter__bottom--option 
        ${
          (choice?.isSelected ?? false) && 'sb-filter__bottom--option--selected'
        }
        ${
          (choice?.isDisabled ?? false) && 'sb-filter__bottom--option--disabled'
        }`}
            data-testid='sb-filter__bottom--option'
            key={`sb-filter__bottom--option-${index}`}
          >
            <Checkfield
              text={choice.label}
              disabled={choice?.isDisabled ?? false}
              checked={choice?.isSelected ? 'Checked' : 'Empty'}
              color='primary'
              type='checkbox'
              size='sm'
              onChange={(event) => {
                const target = event.target as HTMLInputElement
                updateSelected(event, target.value, target.checked)
              }}
              value={choice.value}
            />
          </div>
        ))}
      </>
    )
  }

  return (
    <div
      className={['sb-filter', isSelected, isOpen, isChips, isInline].join(' ')}
      data-testid='sb-filter'
    >
      {label && (
        <label className='sb-filter__label' data-testid='sb-filter__label'>
          {label}
        </label>
      )}
      <div
        className='sb-filter__top'
        data-testid='sb-filter__top'
        onClick={() => {
          setOpen(!open)
        }}
      >
        {chips && !!selected.length ? (
          <div className='sb-filter__results'>
            {selected.map((item, index) => (
              <div
                className='sb-filter__results--option'
                key={`sb-filter__results--option-${index}`}
              >
                {item.label}
                <Icon
                  iconName='x'
                  size={15}
                  className='text-neutral-04 ml-sm cursor-pointer'
                  onClick={(e) => {
                    updateSelected(e, item.value, false)
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className='sb-filter__top--title'>{title}</div>
        )}
        <Icon
          iconName={open ? 'chevron-up' : 'chevron-down'}
          size={20}
          className='text-primary-60'
        />
      </div>
      <div className='sb-filter__bottom' data-testid='sb-filter__bottom'>
        {searchable && !chips && !!selected.length && (
          <div className='sb-filter__bottom--result'>
            <p>Resultado:</p>
            <div className='sb-filter__results'>
              {selected.map((item, index) => (
                <div
                  className='sb-filter__results--option'
                  key={`sb-filter__results--option-${index}`}
                >
                  {item.label}
                  <Icon
                    iconName='x'
                    size={15}
                    className='text-neutral-04 ml-sm cursor-pointer'
                    onClick={(e) => {
                      updateSelected(e, item.value, false)
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {searchable && (
          <div
            className={`sb-filter__bottom--search ${isFocused}`}
            data-testid='sb-filter__bottom--search'
          >
            <input
              placeholder='Placeholder text'
              onChange={filterData}
              onFocus={() => {
                setFocus(true)
              }}
              onBlur={() => {
                setFocus(false)
              }}
              data-testid='sb-filter__bottom--input'
            />
            <Icon
              iconName='search'
              size={24}
              className='text-primary-60 ml-sm'
            />
          </div>
        )}
        <div
          className='sb-filter__bottom--options'
          data-testid='sb-filter__bottom--options'
        >
          {!searching && (
            <div className='sb-filter__bottom--firstoption'>
              <Checkfield
                text='Todos'
                size='sm'
                type='checkbox'
                color='primary'
                value='all'
                checked={stateAll}
                onChange={updateAll}
              />
            </div>
          )}
          {columns && !searchable ? (
            <div
              className='sb-filter__columns'
              data-testid='sb-filter__columns'
            >
              <div className='sb-filter__columns--column'>
                <RenderOptions choices={[...reorder.slice(0, half)]} />
              </div>
              <div className='sb-filter__columns--column'>
                <RenderOptions choices={[...reorder.slice(half)]} />
              </div>
            </div>
          ) : (
            <RenderOptions choices={reorder} />
          )}
        </div>
      </div>
    </div>
  )
}

Filter.propTypes = {
  /**
   * Filter choices
   */
  choices: PropTypes.array,
  /**
   * Filter label
   */
  label: PropTypes.string,
  /**
   * Filter placeholder
   */
  placeholder: PropTypes.string,
  /**
   * searchable
   */
  searchable: PropTypes.bool,
  /**
   * inline
   */
  inline: PropTypes.bool,
  /**
   * columns
   */
  columns: PropTypes.bool,
  /**
   * chips
   */
  chips: PropTypes.bool,
  /**
   * onChange
   */
  onChange: PropTypes.func,
}

Filter.defaultProps = {
  choices: [
    {
      value: '123',
      label:
        'Opción muy grande que aparece en el filter que debe ser controlado. Lorem ipsoum selet amonet os sebut',
    },
    { value: '124', label: 'Opción 2' },
    { value: '125', label: 'Opción 3' },
    { value: '126', label: 'Opción 4', isDisabled: true },
    { value: '127', label: 'Opción 5', isDisabled: true },
    { value: '128', label: 'Opción 6' },
  ],
  label: '',
  placeholder: '',
  inline: false,
  searchable: false,
  columns: false,
  chips: true,
  onChange: () => {},
}

export default Filter
